/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useNavigate, useSearchParams } from 'react-router-dom';
import { type FC, useEffect, useState } from 'react';

import {
  setUserInfo,
  persistSession,
  getPrincipalId,
  type UserInfo,
} from '@trebox-foundation/account-adapter';

// trebox `/v1/auth/oidc/callback` returns the same token shape as
// `/v1/auth/login`. This page receives the JSON body via the browser (the
// backend redirects here after exchanging the IdP code) and persists the
// session token to localStorage before navigating into the app.
//
// In the current backend implementation the callback handler returns the
// token JSON directly. Two delivery modes are accepted:
//   1. `?access_token=…&refresh_token=…` query parameters (simple mode)
//   2. POST-back JSON in window.opener (popup mode · TBD)
// For M1 the simple query-parameter mode is enough.
export const OAuthCallbackPage: FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'pending' | 'ok' | 'error'>('pending');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const error = params.get('error');
    const errorDesc = params.get('error_description');
    if (error) {
      setStatus('error');
      setMessage(errorDesc ? `${error}: ${errorDesc}` : error);
      return;
    }
    const accessToken = params.get('access_token');
    const email = params.get('email') ?? '';
    if (!accessToken) {
      setStatus('error');
      setMessage('missing access_token in callback URL');
      return;
    }
    // Single SoT writer (account-adapter/trebox-session) — also recovers
    // tenant_id / principal_id from the JWT when the callback omits them.
    persistSession(
      accessToken,
      params.get('refresh_token') ?? undefined,
      params.get('tenant_id') ?? undefined,
    );
    const principalId = params.get('principal_id') || getPrincipalId();
    setUserInfo({
      user_id_str: principalId,
      user_id: 0,
      name: email || principalId,
      screen_name: email || principalId,
      email,
      email_collected: !!email,
    } as unknown as UserInfo);
    setStatus('ok');
    navigate('/space', { replace: true });
  }, [params, navigate]);

  return (
    <div
      className="flex items-center justify-center min-h-screen text-[14px]"
      data-testid="oauth.callback"
    >
      {status === 'pending' ? 'Completing sign-in…' : null}
      {status === 'error' ? (
        <div className="flex flex-col items-center gap-[12px]">
          <div>Sign-in failed</div>
          <div className="coze-fg-secondary text-[12px]">{message}</div>
          <a className="coz-fg-hglt" href="/sign">
            Back to sign-in
          </a>
        </div>
      ) : null}
    </div>
  );
};
